import moment from 'moment';

import * as utils from '@utils/form';
import { IMAGE_SIZE } from '@api/media/constants';
import { RELATED_CONTENT_TYPE } from '@constants/relatedContentModules';
import { TITLE_RULE } from '@constants';
import { getImage } from '@utils/media';

export const SIGNED_IN_VALUES = [
  { value: null, label: 'Without message' },
  { value: 'Watch now', label: 'Watch now' },
  { value: 'Watch free', label: 'Watch free' },
  { value: 'Don’t miss', label: 'Don’t miss' },
  { value: 'Watch exclusive', label: 'Watch exclusive' },
  { value: 'Watch highlights', label: 'Watch highlights' },
  { value: 'New to Inside Track', label: 'New to Inside Track' },
];

export const SIGNED_OUT_VALUES = [
  { value: null, label: 'Without message' },
  { value: 'Watch now', label: 'Watch now' },
  { value: 'Watch free', label: 'Watch free' },
  { value: 'Fan exclusive', label: 'Fan exclusive' },
  { value: 'Fan favourite', label: 'Fan favourite' },
  { value: 'Watch highlights', label: 'Watch highlights' },
  { value: 'Sign up to Watch', label: 'Sign up to Watch' },
  { value: 'Inside Track Exclusive', label: 'Inside Track Exclusive' },
  { value: 'Sign up to Inside Track', label: 'Sign up to Inside Track' },
];

export const FORM_FIELD = {
  TITLE: {
    name: 'title',
    label: 'Title:',
    rules: [{ required: true, message: 'Title is required' }, TITLE_RULE],
    placeholder: 'Enter title',
  },
  THUMBNAIL_FEATURE_IMAGE: {
    name: 'thumbnail',
    label: 'Thumbnail image:',
    size: IMAGE_SIZE['1600x1000'],
    serialize: (field, values) => {
      return {
        [`${field.name}Id`]: values[field.name]?.id,
        [`${field.name}Edited`]: values[field.name]?.edited,
        [`${field.name}Title`]: values[field.name]?.title,
      };
    },
    deserialize: (field, data) => {
      const { id, edited, title } = getImage(data, field.name);

      if (!id) return null;

      return { [field.name]: { id, edited, title } };
    },
    deserializeError: utils.deserializeErrorRelatedField,
    rules: [{ required: true, message: 'Thumbnail image is required' }],
  },
  LANGUAGE: {
    name: 'language',
  },
  VIDEO_ID: {
    name: 'videoId',
    label: 'Brightcove Video ID:',
    placeholder: 'Enter Brightcove video ID',
    rules: [{ required: false, message: 'Brightcove Video ID is required' }],
  },
  PLAYER_ID: {
    name: 'playerId',
    label: 'Brightcove Player ID:',
    placeholder: 'Enter Brightcove player ID',
    rules: [{ required: false, message: 'Brightcove Player ID is required' }],
  },
  BRIGHTCOVE_PLAYER_SUPPORTED_COUNTRIES : {
      name: 'brightcoveCountries'
  },
  EBU_PLAYER_URL : {
    name : 'ebuPlayerUrl',
    label : 'EBU Player URL',
    placeholder : 'Enter EBU Player Url',
    rules : [{required : false, message : 'EBU Player Url is required'}]
  },
  EBU_PLAYER_SUPPORTED_COUNTRIES : {
    name : 'ebuCountries'
  },
  STANDFIRST: {
    multiline: true,
    name: 'standFirst',
    label: 'Standfirst (short intro):',
    rules: [{ required: true, message: 'Standfirst is required' }],
  },
  GATED_CONTENT: {
    name: 'isGatedContent',
    label: 'Gated content',
  },
  METATAGS: {
    name: 'metatags',
    label: 'Metatags',
    rules: [{ required: true, message: 'Tags are required' }],
  },
  PUBLICATION_DATE: {
    name: 'date',
    label: 'Publication Date',
    serialize: (field, values) => {
      const value = values[field.name];
      if (value) return { [field.name]: value.toISOString() };
      return null;
    },
    deserialize: (field, serverData) => {
      const value = serverData[field.name];
      if (serverData[field.name]) {
        return {
          [field.name]: moment(Date.parse(value)),
        };
      }
      return {};
    },
  },
  CAMPAIGN: {
    name: 'campaign',
    label: 'Campaign',
    placeholder: 'Add new campaign',
    serialize: utils.relatedEntitySerializer,
    deserialize: utils.relatedEntityDeserializer,
    deserializeError: utils.deserializeErrorRelatedField,
    rules: [{ required: true, message: 'Campaign is required' }],
  },
  SIGNED_IN_CTA: {
    selectOptions: { placeholder: 'Choose for signed in:' },
    options: SIGNED_IN_VALUES,
    name: 'signedInCTA',
    label: 'Signed In',
    defaultValue: null,
  },
  SIGNED_OUT_CTA: {
    selectOptions: { placeholder: 'Choose for signed out:' },
    options: SIGNED_OUT_VALUES,
    name: 'signedOutCTA',
    label: 'Signed Out',
    defaultValue: null,
  },
  RELATED_ATHLETES: {
    name: 'relatedAthlete',
    type: RELATED_CONTENT_TYPE.ATHLETES,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_DISCIPLINES: {
    name: 'relatedDiscipline',
    type: RELATED_CONTENT_TYPE.DISCIPLINES,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_COMPETITIONS: {
    name: 'relatedCompetition',
    type: RELATED_CONTENT_TYPE.COMPETITIONS,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_EVENTS: {
    name: 'relatedEvent',
    type: RELATED_CONTENT_TYPE.EVENTS,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_PLAYLISTS: {
    name: 'relatedPlaylist',
    type: RELATED_CONTENT_TYPE.PLAYLISTS,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
};

export const FORM_FIELDS = Object.values(FORM_FIELD);

export const DEFAULT_VALUES = {
  [FORM_FIELD.SIGNED_IN_CTA.name]: null,
  [FORM_FIELD.SIGNED_OUT_CTA.name]: null,
  [FORM_FIELD.EBU_PLAYER_SUPPORTED_COUNTRIES.name] : [],
  [FORM_FIELD.BRIGHTCOVE_PLAYER_SUPPORTED_COUNTRIES.name] : []
};
